import React, { useEffect, useMemo } from "react";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants.ts";
import SingleFile from "@components/FileUpload/SingleFile.tsx";
import type { UploadEntry } from "@components/FileUpload/uploadStore";
import FormItem from "@components/FormItem/FormItem";
import FormSection from "@components/FormSection/FormSection";
import type { MovementOutletContext } from "@features/vessel-call/vessel-call-status/MovementServices/MovementServicesContainer";
import type { Path } from "react-hook-form";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import { useMovementDocumentUpload } from "../hooks/useMovementDocumentUpload";
import type { MovementServiceFormData } from "../schema/movementServiceFormSchema";
import { ALLOWED_EXTENSIONS } from "./utils";

const LANG_BASE = "vessel-call.movement-services";
const FIELD_NAME = "additionalDocuments" as const;
const ADDITIONAL_DOCUMENTS_MAX_FILES = 25;

type AdditionalDocumentType =
  | "CERTIFICATE"
  | "PERMIT"
  | "DOCUMENT"
  | "INSURANCE"
  | "PRE_SAILING_MASTER_DECLARATION"
  | "VESSEL_MASTER_SOF"
  | "OTHER_SPECIFY";

type AdditionalDocument = {
  name: string;
  url: string;
  certificateType: string;
  idx: number;
  documentType?: AdditionalDocumentType | "";
  otherSpecifyType?: string;
  documentName?: string;
  issueDate?: Date | string | null;
  expiryDate?: Date | string | null;
  documentSummary?: string;
};

const ADDITIONAL_DOCUMENT_TYPES: AdditionalDocumentType[] = [
  "CERTIFICATE",
  "PERMIT",
  "DOCUMENT",
  "INSURANCE",
  "PRE_SAILING_MASTER_DECLARATION",
  "VESSEL_MASTER_SOF",
  "OTHER_SPECIFY",
];

const ADDITIONAL_DOCUMENT_TYPE_LABEL_KEYS: Record<AdditionalDocumentType, string> = {
  CERTIFICATE: "certificate",
  PERMIT: "permit",
  DOCUMENT: "document",
  INSURANCE: "insurance",
  PRE_SAILING_MASTER_DECLARATION: "pre-sailing-master-declaration",
  VESSEL_MASTER_SOF: "vessel-master-sof",
  OTHER_SPECIFY: "other-specify",
};

const generateAdditionalDocumentField = {
  documentType: (idx: number) =>
    `${FIELD_NAME}.${idx}.documentType` as Path<MovementServiceFormData>,
  otherSpecifyType: (idx: number) =>
    `${FIELD_NAME}.${idx}.otherSpecifyType` as Path<MovementServiceFormData>,
  documentName: (idx: number) =>
    `${FIELD_NAME}.${idx}.documentName` as Path<MovementServiceFormData>,
  issueDate: (idx: number) => `${FIELD_NAME}.${idx}.issueDate` as Path<MovementServiceFormData>,
  expiryDate: (idx: number) => `${FIELD_NAME}.${idx}.expiryDate` as Path<MovementServiceFormData>,
  documentSummary: (idx: number) =>
    `${FIELD_NAME}.${idx}.documentSummary` as Path<MovementServiceFormData>,
};

const buildDateMinValue = (dateValue: unknown): Date | undefined => {
  if (!dateValue) return undefined;
  const date = new Date(dateValue as string | Date);
  if (Number.isNaN(date.getTime())) return undefined;
  date.setDate(date.getDate() + 1);
  return date;
};

const hasDateValue = (value: unknown): boolean => {
  if (value == null) return false;
  if (typeof value === "string") return value.trim().length > 0;
  return true;
};

const pickNonEmptyString = (primary: unknown, fallback: string | undefined): string => {
  if (typeof primary === "string" && primary.trim().length > 0) {
    return primary;
  }

  return fallback ?? "";
};

const pickDateValue = (
  primary: AdditionalDocument["issueDate"] | AdditionalDocument["expiryDate"],
  fallback: AdditionalDocument["issueDate"] | AdditionalDocument["expiryDate"],
): AdditionalDocument["issueDate"] | AdditionalDocument["expiryDate"] => {
  if (hasDateValue(primary)) return primary;
  if (hasDateValue(fallback)) return fallback;
  return null;
};

const MovementAdditionalDocuments: React.FC = () => {
  const { t } = useTranslation();
  const { formData, updateFormData } = useOutletContext<MovementOutletContext>();
  const FILE_UPLOAD_PROFILES = useFileUploadProfiles();

  const watchedAdditionalDocumentsRaw = formData.watch(FIELD_NAME) as
    | AdditionalDocument[]
    | undefined;
  const watchedAdditionalDocuments = useMemo(
    () => watchedAdditionalDocumentsRaw ?? [],
    [watchedAdditionalDocumentsRaw],
  );

  const additionalDocumentTypeOptions = useMemo(
    () =>
      ADDITIONAL_DOCUMENT_TYPES.map((value) => ({
        label: t(
          `${LANG_BASE}.additional-documents.document-types.${ADDITIONAL_DOCUMENT_TYPE_LABEL_KEYS[value]}`,
        ),
        value,
      })),
    [t],
  );

  const { files, setFiles, handleFieldChangeForAzure, removeFileAtIndex, isLoading } =
    useMovementDocumentUpload({
      fieldName: FIELD_NAME,
      certificateType: "ADDITIONAL DOCUMENTS",
      formData,
      updateFormData,
    });

  type UpdateFormDataKey = Parameters<MovementOutletContext["updateFormData"]>[0];
  type UpdateFormDataValue = Parameters<MovementOutletContext["updateFormData"]>[1];

  const syncMetadataToUploadStore = (idx: number, patch: Partial<AdditionalDocument>) => {
    setFiles((prev) =>
      prev.map((entry, entryIdx) => {
        if (entryIdx !== idx || entry instanceof File) {
          return entry;
        }

        return {
          ...(entry as UploadEntry),
          ...patch,
        } as UploadEntry;
      }),
    );
  };

  const onMetadataFieldChange = (key: UpdateFormDataKey, value: UpdateFormDataValue) => {
    updateFormData(key, value);

    const match =
      /^additionalDocuments\.(\d+)\.(documentType|otherSpecifyType|documentName|issueDate|expiryDate|documentSummary)$/.exec(
        String(key),
      );

    if (!match) return;

    const idx = Number(match[1]);
    const field = match[2] as keyof Pick<
      AdditionalDocument,
      | "documentType"
      | "otherSpecifyType"
      | "documentName"
      | "issueDate"
      | "expiryDate"
      | "documentSummary"
    >;

    const patch: Partial<AdditionalDocument> = {
      [field]: value,
    };

    if (field === "documentType" && value !== "OTHER_SPECIFY") {
      patch.otherSpecifyType = "";
    }

    syncMetadataToUploadStore(idx, patch);
  };

  useEffect(() => {
    setFiles((prev) => {
      let changed = false;

      const next = prev.map((entry, idx) => {
        if (entry instanceof File) {
          return entry;
        }

        const watched = watchedAdditionalDocuments[idx];
        if (!watched || watched instanceof File) {
          return entry;
        }

        const current = entry as AdditionalDocument;
        const documentType = pickNonEmptyString(watched.documentType, current.documentType);
        const otherSpecifyType =
          documentType === "OTHER_SPECIFY"
            ? pickNonEmptyString(watched.otherSpecifyType, current.otherSpecifyType)
            : "";

        const merged = {
          ...entry,
          documentType,
          otherSpecifyType,
          documentName: pickNonEmptyString(watched.documentName, current.documentName),
          issueDate: pickDateValue(watched.issueDate, current.issueDate),
          expiryDate: pickDateValue(watched.expiryDate, current.expiryDate),
          documentSummary: pickNonEmptyString(watched.documentSummary, current.documentSummary),
        };

        const hasChanged =
          current.documentType !== merged.documentType ||
          current.otherSpecifyType !== merged.otherSpecifyType ||
          current.documentName !== merged.documentName ||
          current.issueDate !== merged.issueDate ||
          current.expiryDate !== merged.expiryDate ||
          current.documentSummary !== merged.documentSummary;

        if (hasChanged) {
          changed = true;
          return merged;
        }

        return entry;
      });

      return changed ? next : prev;
    });
  }, [setFiles, watchedAdditionalDocuments]);

  const renderAdditionalDocumentMetadata = (idx: number) => {
    const document = watchedAdditionalDocuments[idx];
    const documentType = document?.documentType;
    const issueDate = document?.issueDate;

    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <FormItem<MovementServiceFormData>
          testId={`movement-additional-documents-${idx}-document-type`}
          name={generateAdditionalDocumentField.documentType(idx) as any}
          type="select"
          options={additionalDocumentTypeOptions}
          label={t(`${LANG_BASE}.additional-documents.document-type`)}
          onFieldChange={onMetadataFieldChange}
        />
        {documentType === "OTHER_SPECIFY" && (
          <FormItem<MovementServiceFormData>
            testId={`movement-additional-documents-${idx}-other-specify`}
            name={generateAdditionalDocumentField.otherSpecifyType(idx) as any}
            type="text"
            maxChars={30}
            label={t(`${LANG_BASE}.additional-documents.other-specify`)}
            onFieldChange={onMetadataFieldChange}
          />
        )}
        {documentType !== "OTHER_SPECIFY" && <div className="hidden lg:block" aria-hidden="true" />}
        <div className="lg:col-span-2">
          <FormItem<MovementServiceFormData>
            testId={`movement-additional-documents-${idx}-document-name`}
            name={generateAdditionalDocumentField.documentName(idx) as any}
            type="text"
            maxChars={50}
            label={t(`${LANG_BASE}.additional-documents.document-name`)}
            onFieldChange={onMetadataFieldChange}
          />
        </div>
        <FormItem<MovementServiceFormData>
          testId={`movement-additional-documents-${idx}-issue-date`}
          name={generateAdditionalDocumentField.issueDate(idx) as any}
          type="date-picker"
          label={t(`${LANG_BASE}.additional-documents.issue-date`)}
          onFieldChange={(key, value) => {
            onMetadataFieldChange(key, value);
            formData.trigger([
              generateAdditionalDocumentField.issueDate(idx) as any,
              generateAdditionalDocumentField.expiryDate(idx) as any,
            ]);
          }}
          timePicker={false}
          dateConstraint="not-future"
        />
        <FormItem<MovementServiceFormData>
          testId={`movement-additional-documents-${idx}-expiry-date`}
          name={generateAdditionalDocumentField.expiryDate(idx) as any}
          type="date-picker"
          label={t(`${LANG_BASE}.additional-documents.expiry-date`)}
          onFieldChange={(key, value) => {
            onMetadataFieldChange(key, value);
            formData.trigger([
              generateAdditionalDocumentField.issueDate(idx) as any,
              generateAdditionalDocumentField.expiryDate(idx) as any,
            ]);
          }}
          timePicker={false}
          dateConstraint="not-past"
          minDate={buildDateMinValue(issueDate)}
        />
        <div className="lg:col-span-2">
          <FormItem<MovementServiceFormData>
            testId={`movement-additional-documents-${idx}-document-summary`}
            name={generateAdditionalDocumentField.documentSummary(idx) as any}
            type="text"
            maxChars={300}
            label={t(`${LANG_BASE}.additional-documents.document-summary`)}
            onFieldChange={onMetadataFieldChange}
          />
        </div>
      </div>
    );
  };

  return (
    <FormProvider {...formData}>
      <FormSection title={t(`${LANG_BASE}.sections.additional-documents`)}>
        <div className="col-span-full">
          <FormItem<MovementServiceFormData, typeof FIELD_NAME>
            name={FIELD_NAME}
            type="file"
            testId="movement-additional-documents"
            notSuccess
            uploadConditions={FILE_UPLOAD_PROFILES.additionalDocuments}
            allowedFileExtensions={ALLOWED_EXTENSIONS}
            maxFiles={ADDITIONAL_DOCUMENTS_MAX_FILES}
            files={files}
            setFiles={setFiles}
            uploadDescription={t("upload.dragOrDropDescription")}
            onUpload={handleFieldChangeForAzure}
          />
        </div>
        {files.length
          ? files.map((file, idx) => {
              const hasUploadedUrl = Boolean((file as Record<string, unknown>).url);
              return (
                <div
                  className="col-span-full grid grid-cols-1 gap-4"
                  key={`${file.name}-${(file as File).lastModified ?? idx}`}
                >
                  <SingleFile
                    testId={`${FIELD_NAME}-${idx}`}
                    file={file}
                    currentFileIndex={idx}
                    fieldName={FIELD_NAME}
                    setFiles={() => undefined}
                    removeFn={() => removeFileAtIndex(idx)}
                    isLoading={isLoading && file instanceof File}
                  />
                  {hasUploadedUrl && renderAdditionalDocumentMetadata(idx)}
                </div>
              );
            })
          : null}
      </FormSection>
    </FormProvider>
  );
};

export default MovementAdditionalDocuments;
